export type EnemyKind = 'squid' | 'crab' | 'octopus';

export interface SceneLayoutConfig {
  canvasWidth: number;
  canvasHeight: number;
  scale?: number;
  rows?: number;
  cols?: number;
  spacingX?: number;
  spacingY?: number;
  spriteWidth?: number;
  spriteHeight?: number;
  gridTopMargin?: number;
  playerBottomMargin?: number;
}

export interface EnemyLayoutItem {
  row: number;
  col: number;
  kind: EnemyKind;
  textureKey: string;
  x: number;
  y: number;
  tint: number;
  scale: number;
}

export interface PlayerLayoutItem {
  textureKey: string;
  x: number;
  y: number;
  tint: number;
  scale: number;
}

export interface SceneLayout {
  player: PlayerLayoutItem;
  enemies: EnemyLayoutItem[];
}

const TINT_GREEN = 0x22ff22;
const TINT_WHITE = 0xffffff;

function kindForRow(row: number): EnemyKind {
  if (row === 0) return 'squid';
  if (row <= 2) return 'crab';
  return 'octopus';
}

export function computeSceneLayout(config: SceneLayoutConfig): SceneLayout {
  const scale = config.scale ?? 3;
  const rows = config.rows ?? 5;
  const cols = config.cols ?? 11;
  const spacingX = config.spacingX ?? 16;
  const spacingY = config.spacingY ?? 16;
  const spriteWidth = config.spriteWidth ?? 16;
  const spriteHeight = config.spriteHeight ?? 8;
  const gridTopMargin = config.gridTopMargin ?? 80;
  const playerBottomMargin = config.playerBottomMargin ?? 60;

  const stepX = spacingX * scale;
  const stepY = spacingY * scale;
  const spriteScreenW = spriteWidth * scale;
  const spriteScreenH = spriteHeight * scale;

  const gridWidth = (cols - 1) * stepX + spriteScreenW;
  const gridLeft = Math.round((config.canvasWidth - gridWidth) / 2);

  const enemies: EnemyLayoutItem[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const kind = kindForRow(row);
      enemies.push({
        row,
        col,
        kind,
        textureKey: `invader_${kind}_a`,
        x: gridLeft + col * stepX,
        y: gridTopMargin + row * stepY,
        tint: kind === 'octopus' ? TINT_GREEN : TINT_WHITE,
        scale,
      });
    }
  }

  const player: PlayerLayoutItem = {
    textureKey: 'player',
    x: Math.round((config.canvasWidth - spriteScreenW) / 2),
    y: config.canvasHeight - playerBottomMargin - spriteScreenH,
    tint: TINT_GREEN,
    scale,
  };

  return { player, enemies };
}
